import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePlaidLink } from 'react-plaid-link'
import { useNavigate } from '@tanstack/react-router'
import { exchangePublicToken } from './plaid-link'

/**
 * Plaid OAuth resume page.
 *
 * Plaid's web flow for OAuth institutions (BofA, Chase, etc.):
 *   1. User clicks "Add bank" → we mint a link_token with
 *      redirect_uri = https://budget.crawfishlabs.ai/plaid-oauth.
 *   2. Plaid Link opens, user picks an OAuth bank, gets bounced to
 *      the bank, authenticates, gets bounced back to /plaid-oauth?
 *      oauth_state_id=... on THIS site.
 *   3. This page reads the original link_token from sessionStorage
 *      and calls usePlaidLink with `receivedRedirectUri = window.
 *      location.href`. The SDK auto-resumes the session, completes
 *      it in-page, and fires `onSuccess` with a public_token.
 *   4. We exchange the public_token (same path the regular flow
 *      uses) and bounce the user back to /accounts.
 *
 * If sessionStorage is empty (user opened the URL in a different
 * tab, came back hours later, etc.) we can't resume — show a
 * friendly error and a link back to Accounts.
 */
export function PlaidOAuthResume() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const stored = sessionStorage.getItem('crawfish.budget.plaid.linkToken')
    if (!stored) {
      setError(
        "Couldn't resume the bank-link flow. Open Accounts and start " +
          'over — your session may have expired.',
      )
      return
    }
    setLinkToken(stored)
  }, [])

  const exchange = useMutation({
    mutationFn: async (publicToken: string) => exchangePublicToken(publicToken),
    onSuccess: () => {
      sessionStorage.removeItem('crawfish.budget.plaid.linkToken')
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['plaid_items'] })
      void navigate({ to: '/accounts' })
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : 'Exchange failed; please try again.'),
  })

  const plaid = usePlaidLink({
    token: linkToken,
    receivedRedirectUri: typeof window !== 'undefined' ? window.location.href : undefined,
    onSuccess: (publicToken) => {
      exchange.mutate(publicToken)
    },
    onExit: (err) => {
      if (err) {
        setError(err.error_message ?? err.error_code ?? 'Link cancelled')
      } else {
        // User dismissed; send them back to Accounts cleanly.
        void navigate({ to: '/accounts' })
      }
    },
  })

  // Auto-open the resume sheet once the SDK is ready.
  useEffect(() => {
    if (linkToken && plaid.ready) plaid.open()
  }, [linkToken, plaid])

  return (
    <div className="mx-auto max-w-md space-y-4 py-12 text-center">
      <h1 className="text-xl font-semibold">Finishing bank link…</h1>
      {error ? (
        <>
          <p role="alert" className="text-sm text-overspent">
            {error}
          </p>
          <button
            type="button"
            onClick={() => void navigate({ to: '/accounts' })}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Back to Accounts
          </button>
        </>
      ) : (
        <p className="text-sm text-zinc-500">
          Hang on while we wrap up with your bank — this usually takes a couple seconds.
        </p>
      )}
    </div>
  )
}
